import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { EMPTY_LIST } from '../../../constants/common'
import { HTML_LINK_TARGET } from '../../../constants/html'
import {
  QA_NAME_ATTACH_BTN,
  QA_NAME_CANCEL_BTN,
  QA_NAME_CLEAR_BTN,
} from '../../../constants/qa'
import {
  useInitData,
  useInlineNotifications,
  useLinkLikeClick,
  useValidationWithoutFormikNotifications,
} from '../../../hooks'
import { getItemsIds } from '../../../utils/common'
import { TableRowActions } from '../../Table'
import {
  TableModal,
  useTableModalCloseHandler,
  useTableModalLoadingData,
} from '../../TableModal'

import {
  EMAIL_ATTACHED_THREADS_MODAL_TABLE_ROW_ACTIONS_CONFIG,
  QA_NAME_EMAIL_ATTACHED_THREADS_MODAL,
} from './EmailAttachedThreadsModal.constants'
import {
  getEmailAttachedThreadLink,
  getEmailAttachedThreadsModalTableColumns,
  getEmailAttachedThreadsNavigationContext,
  getSelectedItems,
  isEqualEmailAttachedThreadsModal,
} from './EmailAttachedThreadsModal.utils'
import { EmailAttachedThreadsModalTableRow } from './EmailThreadsModalTableRow'

import * as styles from './EmailAttachedThreadsModal.module.css'

import EmailIcon from '../../../icons/email.svg'

function EmailAttachedThreadsModal({
  actionsProps,
  attachedFilesProps,
  counterProps,
  entityId,
  getAttachedEmailThreads,
  getItemsFromResponse,
  headerProps,
  initialSelection,
  loadAttachedItems,
  loadItems,
  readonly,
  renderThreadModal,
  onAttach,
  onAttachToForm,
  onClose,
  ...restProps
}) {
  const { t } = useTranslation()

  const { availableSections } = useInitData()

  const [initialValues, setInitialValues] = useState(
    initialSelection || EMPTY_LIST
  )

  const [openThreadUuid, setOpenThreadUuid] = useState(null)

  const {
    allItems,
    checkIsItemSelected,
    exclude,
    excludeIds,
    handleChangePagination,
    handleExclude,
    handleLoadItems,
    handleSaveInitialSelection,
    handleSelect,
    handleSelectAll,
    handleSetOrdering,
    handleUnselectAllPages,
    initialSelectionValues,
    isAllSelected,
    isRemoveSelection,
    loading,
    ordering,
    pagination,
    selectAllPages,
    selected,
    setLoading,
    tableItems,
  } = useTableModalLoadingData({
    entityId,
    getItemsFromResponse,
    initialSelection,
    loadAttachedItems,
    loadItems,
    tableRowActionsConfig:
      EMAIL_ATTACHED_THREADS_MODAL_TABLE_ROW_ACTIONS_CONFIG,
    onAfterLoadItems: !readonly ? handleInitializeAttachedItems : null,
    ...restProps,
  })

  const { handleCloseTableModal, isCloseConfirm, setCloseConfirm } =
    useTableModalCloseHandler({
      initialSelectionValues,
      selectAllPages,
      selected,
      onClose,
    })

  const { handleClick, handleMouseDown } = useLinkLikeClick()

  const { handleAddNotification, handleRemoveNotification, notifications } =
    useInlineNotifications()

  const preparedSelectedItems = getSelectedItems({
    allItems,
    excludeIds,
    selectAllPages,
    selected,
  })

  const preparedSelectedIds = getItemsIds(
    preparedSelectedItems,
    EMAIL_ATTACHED_THREADS_MODAL_TABLE_ROW_ACTIONS_CONFIG.itemIdKey
  )

  const { isValidForm, setNotificationListRef } =
    useValidationWithoutFormikNotifications({
      validationSchemaProps: {
        initialValues,
        isEqual: isEqualEmailAttachedThreadsModal,
        isEqualProps: { preparedSelectedIds },
      },
      onRemoveNotification: handleRemoveNotification,
      onSetNotification: handleAddNotification,
    })

  function handleAttach() {
    if (!isValidForm()) {
      return
    }

    if (onAttach) {
      setLoading(true)

      onAttach(preparedSelectedIds).finally(() => setLoading(false))
    }

    if (onAttachToForm) {
      onAttachToForm(preparedSelectedItems)
    }
  }

  const tableColumns = getEmailAttachedThreadsModalTableColumns()

  function handleInitializeAttachedItems() {
    return loadAttachedItems?.().then(({ data }) => {
      const attachedItems = getAttachedEmailThreads(data)

      if (attachedItems?.length > 0) {
        handleSaveInitialSelection(attachedItems)

        setInitialValues(attachedItems)
      }
    })
  }

  function handleClickRow(id, event) {
    if (renderThreadModal) {
      setOpenThreadUuid(id)

      return
    }

    handleClick({
      event,
      link: getEmailAttachedThreadLink({ availableSections, entityId, id }),
      target: HTML_LINK_TARGET.blank,
    })
  }

  function handleMouseDownRow(id, event) {
    if (!renderThreadModal) {
      handleMouseDown({
        event,
        link: getEmailAttachedThreadLink({ availableSections, entityId, id }),
      })
    }
  }

  function handleCloseThreadModal() {
    setOpenThreadUuid(null)
  }

  return (
    <>
      <TableModal
        {...(counterProps
          ? { counterProps: { ...counterProps, count: pagination?.count } }
          : null)}
        initialFullScreen
        Search={null}
        actionsProps={{
          ...(readonly ? { ButtonCancel: null } : null),
          buttonAttachProps: {
            ...actionsProps?.buttonAttachProps,
            disabled: !selected,
            isLoading: loading,
            qaName: QA_NAME_ATTACH_BTN,
            text:
              actionsProps?.buttonAttachProps?.text || 'Modal.ConfirmBtnKey',
            onClick:
              readonly || tableItems?.items?.length === 0
                ? onClose
                : handleAttach,
          },
          buttonCancelProps: {
            qaName: QA_NAME_CANCEL_BTN,
            onClick: handleCloseTableModal,
          },
          buttonClearProps: {
            qaName: QA_NAME_CLEAR_BTN,
            onClick: handleUnselectAllPages,
          },
        }}
        contentProps={{ loading }}
        emptyStateProps={{
          EntityIcon: EmailIcon,
          className: styles.emptyState,
          text: t('Email.EmailThreadsNoDataKey'),
        }}
        headerProps={{
          ...headerProps,
          text: headerProps?.text || 'Modal.EmailAttachThreadsTitleKey',
          onClose: handleCloseTableModal,
        }}
        isCloseConfirm={isCloseConfirm}
        loading={loading}
        notificationListProps={{
          ref: setNotificationListRef,
        }}
        notifications={notifications}
        paginationProps={{ pagination, onChange: handleChangePagination }}
        qaName={QA_NAME_EMAIL_ATTACHED_THREADS_MODAL}
        selectAllPages={selectAllPages}
        selected={selected}
        selectionToolbarProps={{
          exclude,
          pagination,
          selectAllPages,
          selected,
          onClose: handleUnselectAllPages,
        }}
        setCloseConfirm={setCloseConfirm}
        tableItems={tableItems?.items}
        tableProps={{
          ...(readonly ? { actionsColumnWidth: 0 } : null),
          TableRow: EmailAttachedThreadsModalTableRow,
          checkIsItemSelected,
          className: styles.table,
          columns: tableColumns,
          headerRowProps: {
            Actions: TableRowActions,
            actionsProps: {
              checked: isAllSelected,
              selected: isRemoveSelection,
              onChange: handleSelectAll,
            },
            ordering,
            resizable: false,
            withCellBorder: false,
            onSetOrdering: handleSetOrdering,
          },
          items: tableItems?.items,
          rowProps: {
            ...(readonly ? { Actions: null } : null),
            attachedFilesProps,
            readonly,
            onClick: handleClickRow,
            onMouseDown: handleMouseDownRow,
            onSelect: selectAllPages ? handleExclude : handleSelect,
          },
        }}
        onApplyConfirmModal={onClose}
        onClose={onClose}
        onRefresh={handleLoadItems}
        onTableModalClose={handleCloseTableModal}
      />
      {openThreadUuid && renderThreadModal
        ? renderThreadModal({
            navigationContext: getEmailAttachedThreadsNavigationContext({
              entityId,
              tableItems,
            }),
            uuid: openThreadUuid,
            onClose: handleCloseThreadModal,
            onReload: handleLoadItems,
          })
        : null}
    </>
  )
}

export default EmailAttachedThreadsModal
